import React from "react";
import Paper from "@mui/material/Paper";
import isPropsEqual from "react-fast-compare";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import { SelectChangeEvent } from "@mui/material/Select";
import { TextFieldProps } from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import CalculationsWrkCmp from "../../containers/QuotesCreate/CalculationsWrkCmp";
import Section from "../forms/Section";
import FormControl, { FormHelperText } from "../forms/FormControl";
import TextField from "../forms/TextInput";
import Select, { MenuItem } from "../forms/SelectInput";
import { useNumberFormatter } from "../../hooks";
import type {
  WorkCategory,
  WorkOccupation,
  ClaimHistoryItem,
  WrkCmpOptionalExtra,
  OptionalExtraForm,
  WrkCmpCalculationsType,
} from "../../lib/types";
import Stack from "@mui/material/Stack";
import { Tooltip } from "@mui/material";

function ReadOnlyText({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: TextFieldProps["color"];
}) {
  return (
    <Stack direction="column">
      <Typography variant="caption" color={color || "rgba(0,0,0,0.6)"}>
        {label}
      </Typography>
      <Typography variant="body1" color={color || "rgba(0,0,0,0.87)"}>
        {value}
      </Typography>
    </Stack>
  );
}

function ClaimsHistoryAddButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="body2" color="GrayText">
        {!disabled && (
          <em>Press the plus button to add a claims history item.</em>
        )}
        {disabled && <em>Canot add any more claims history items.</em>}
      </Typography>
      <Tooltip
        title={disabled ? "" : "Click to add a claims history item"}
        placement="right"
      >
        <IconButton
          disabled={disabled}
          onClick={onClick}
          aria-label="add claims history item"
          color="primary"
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

interface Props {
  // Page props
  color: string;
  subTitle: string;
  title: string;
  isLoadingMain: boolean;

  // Quote Data (categories and occupations)
  isLoadingCategories: boolean;
  isLoadingOccupations: boolean;
  wageRoll: number;
  claimsHistory: ClaimHistoryItem[];
  selectedCategory: WorkCategory | null;
  selectedOccupation: WorkOccupation | null;
  categories: Array<WorkCategory>;
  occupations: Array<WorkOccupation>;
  optionalExtras: Record<WrkCmpOptionalExtra, OptionalExtraForm>;
  selectedOptionalExtras: Record<WrkCmpOptionalExtra, number>; // Where number is the index of the selected option
  selectedOptionalExtraAmounts: Record<WrkCmpOptionalExtra, number>; // Where number is the amount (i.e. Premium Sum * Percentage)

  // Quote calculation props
  minPremium: number;
  calculations: WrkCmpCalculationsType;
  stampDuty: number;

  // Quote form values

  // Event handlers
  onChangeCategory: ({
    categoryId,
  }: {
    categoryId: WorkCategory["id"];
  }) => void;
  onChangeOccupation: ({
    occupationId,
  }: {
    occupationId: WorkOccupation["id"];
  }) => void;
  onChangeWageRollText: ({ value }: { value: number }) => void;
  onAddClaimsHistoryItem: () => void;
  onDeleteClaimsHistoryItem: (id: ClaimHistoryItem["id"]) => void;
  onChangeClaimHistoryClaimItemText: ({
    id,
    field,
    value,
  }: {
    id: ClaimHistoryItem["id"];
    field: "claim_count" | "claim_amount";
    value: number;
  }) => void;
  onChangeOptionalExtra: ({
    extra,
    optionIndex,
  }: {
    extra: WrkCmpOptionalExtra;
    optionIndex: number;
  }) => void;
}

function WorkersCompensationForm({
  // Page props
  color,
  subTitle,
  title,
  isLoadingMain,

  // Quote Data (categories and occupations)
  isLoadingCategories,
  isLoadingOccupations,
  selectedCategory,
  selectedOccupation,
  wageRoll,
  claimsHistory,
  categories,
  occupations,
  optionalExtras,
  selectedOptionalExtras,
  selectedOptionalExtraAmounts,

  // Quote calculation props
  minPremium,
  calculations,
  stampDuty,

  // Event handlers
  onChangeCategory,
  onChangeOccupation,
  onChangeWageRollText,
  onAddClaimsHistoryItem,
  onDeleteClaimsHistoryItem,
  onChangeClaimHistoryClaimItemText,
  onChangeOptionalExtra,
}: Props) {
  const moneyFormatter = useNumberFormatter();
  const percentFormatter = useNumberFormatter({
    style: "percent",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const decimalFormatter = useNumberFormatter({
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  const handleChangeCategory = (event: SelectChangeEvent) => {
    const { value } = event.target;
    onChangeCategory({ categoryId: +value });
  };

  const handleChangeOccupation = (event: SelectChangeEvent) => {
    const { value } = event.target;
    onChangeOccupation({ occupationId: +value });
  };

  const handleChangeWageRollText = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    onChangeWageRollText({ value: +value });
  };

  const handleDeleteClaimsHistoryItem = (id: ClaimHistoryItem["id"]) => () => {
    onDeleteClaimsHistoryItem(id);
  };

  const handleChangeClaimHistoryItemText =
    (id: ClaimHistoryItem["id"], field: "claim_amount" | "claim_count") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onChangeClaimHistoryClaimItemText({ id, field, value: +value });
    };

  const handleChangeOptionalExtra =
    (extra: WrkCmpOptionalExtra) =>
    (event: SelectChangeEvent | React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onChangeOptionalExtra({ extra, optionIndex: +value });
    };

  const uninsurableOccupation = selectedOccupation?.rate === null;

  const inputFields = {
    mainFields: [
      {
        id: "category",
        type: "select",
        props: {
          label: "Category",
          loading: isLoadingCategories,
          disabled: isLoadingCategories,
          value: selectedCategory?.id || "",
          handleChange: handleChangeCategory,
          options: categories.map((category) => ({
            value: category.id,
            label: category.category_name,
          })),
          grid: {
            xs: 12,
          },
        },
      },
      {
        id: "occupations",
        type: "select",
        props: {
          label: "Occupation",
          loading: isLoadingOccupations,
          disabled: isLoadingOccupations,
          value: selectedOccupation?.id || "",
          handleChange: handleChangeOccupation,
          options: occupations.map((occupation) => ({
            value: occupation.id,
            label: occupation.occupation_name,
          })),
          grid: {
            xs: 12,
          },
        },
      },
      {
        id: "wage_roll",
        type: "input",
        props: {
          label: "Wage Roll",
          name: "wageRoll",
          value: wageRoll,
          onChange: handleChangeWageRollText,
          disabled:
            uninsurableOccupation ||
            isLoadingMain ||
            isLoadingOccupations ||
            isLoadingCategories,
          grid: {
            xs: 12,
            lg: 9,
          },
        },
      },
      {
        id: "occupation_rate",
        type: "input",
        props: {
          label: "Rate (%)",
          value: selectedOccupation?.rate ? selectedOccupation.rate : "",
          type: "percent",
          readOnly: true,
          grid: {
            xs: 12,
            lg: 3,
            sx: { textAlign: "center" },
          },
        },
      },
    ],
    optionalExtrasFields: [
      {
        id: "hausKrai",
        select: {
          label: "Haus Krai",
          loading: false,
          value: selectedOptionalExtras["Haus Krai"],
          handleChange: handleChangeOptionalExtra("Haus Krai"),
          options: optionalExtras["Haus Krai"].data,
          optionDisplay: (val: number) =>
            val === 0 ? "No" : moneyFormatter.format(val),
          grid: {
            xs: 12,
            lg: 6,
          },
        },
        input: {
          name: "hausKraiValue",
          label: "Amount (SBD)",
          value: selectedOptionalExtraAmounts["Haus Krai"],
          readOnly: true,
          type: "number",
          min: "0",
          grid: {
            xs: 12,
            lg: 6,
          },
        },
      },
      {
        id: "wetha",
        select: {
          label: "WETHA",
          loading: false,
          value: selectedOptionalExtras["WETHA"],
          handleChange: handleChangeOptionalExtra("WETHA"),
          options: optionalExtras["WETHA"].data,
          grid: {
            xs: 12,
            lg: 6,
          },
        },
        input: {
          name: "wethaValue",
          label: "Amount (SBD)",
          value: selectedOptionalExtraAmounts["WETHA"],
          readOnly: true,
          type: "number",
          min: "0",
          grid: {
            xs: 12,
            lg: 6,
          },
        },
      },
      {
        id: "bekimPei",
        select: {
          label: "BEKIM PEI",
          value: selectedOptionalExtras["BEKIM PEI"],
          loading: false,
          handleChange: handleChangeOptionalExtra("BEKIM PEI"),
          options: optionalExtras["BEKIM PEI"].data,
          grid: {
            xs: 12,
            lg: 6,
          },
        },
        input: {
          name: "bekimPeiValue",
          label: "Amount (SBD)",
          value: selectedOptionalExtraAmounts["BEKIM PEI"],
          readOnly: true,
          type: "number",
          min: "0",
          grid: {
            xs: 12,
            lg: 6,
          },
        },
      },
      {
        id: "commonLaw",
        select: {
          label: "Common Law",
          loading: false,
          value: selectedOptionalExtras["Common Law"],
          handleChange: handleChangeOptionalExtra("Common Law"),
          options: optionalExtras["Common Law"].data,
          grid: {
            xs: 12,
            lg: 6,
          },
        },
        input: {
          name: "commonLawValue",
          label: "Amount (SBD)",
          value: selectedOptionalExtraAmounts["Common Law"],
          readOnly: true,
          type: "number",
          min: "0",
          grid: {
            xs: 12,
            lg: 6,
          },
        },
      },
    ],
  };

  if (isLoadingMain) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            my={2}
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <Box mr={2}>
              <Typography component="p" variant="body1">
                <em>Loading data</em>
              </Typography>
            </Box>
            <CircularProgress size={24} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 120,
          zIndex: 0,
          backgroundImage: `linear-gradient(to top, rgba(255,0,0,0), ${color})`,
        }}
      />

      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Typography variant="h6" component="h1" sx={{ mb: 2, mt: 2, px: 2 }}>
            {title}
          </Typography>
        </Grid>
      </Grid>

      <form id="quoteform" autoComplete="off">
        <Grid container spacing={2} p={2}>
          <Grid item xs={12}>
            <Section title={`${subTitle} Details`} bottomBorder={false}>
              <Grid container spacing={2}>
                {/**
                 * Main Details
                 **/}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {inputFields.mainFields.map(({ id, type, props }) => {
                      return (
                        <React.Fragment key={id}>
                          <Grid item {...props.grid}>
                            {type === "select" && props.options && (
                              <FormControl sx={{ width: "100%" }}>
                                <FormHelperText>
                                  {`${props.label} `}
                                  {props.loading ? <em>(Loading ...)</em> : ""}
                                </FormHelperText>
                                <Select
                                  variant="outlined"
                                  value={props.value}
                                  onChange={props.handleChange as any}
                                  disabled={props.disabled}
                                >
                                  {props.options.map(
                                    ({ value, label }, count) => (
                                      <MenuItem key={count} value={value}>
                                        {label}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              </FormControl>
                            )}
                            {type === "input" &&
                              props.readOnly &&
                              (props.type === "percent" ||
                                props.type === "decimal") && (
                                <>
                                  <ReadOnlyText
                                    label={props.label}
                                    value={
                                      isNaN(+props.value)
                                        ? "0.00%"
                                        : props.type === "percent"
                                        ? percentFormatter.format(+props.value)
                                        : decimalFormatter.format(+props.value)
                                    }
                                  />
                                </>
                              )}
                            {type === "input" && !props.readOnly && (
                              <TextField
                                autoComplete="off"
                                name={props.name}
                                variant="outlined"
                                sx={{ width: "100%" }}
                                {...props}
                              />
                            )}
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                    {uninsurableOccupation && (
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component="p"
                          color="error"
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                          }}
                        >
                          <em>
                            Occupation Unacceptable - DO NOT WRITE BUSINESS
                          </em>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Section>
          </Grid>

          {/**
           * Claims History
           **/}
          <Grid item xs={12}>
            <Section subsection title="Claims History">
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  <Table aria-label="Claims History Table" size="small">
                    <TableBody>
                      {claimsHistory.map((claim) => (
                        <TableRow
                          key={claim.year}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>Year {claim.year}</TableCell>
                          <TableCell align="right">
                            <TextField
                              autoComplete="off"
                              name={`NoOfClaims${claim.id}`}
                              label="No. of Claims"
                              variant="outlined"
                              sx={{ width: "100%" }}
                              size="small"
                              value={claim.claim_count}
                              onChange={handleChangeClaimHistoryItemText(
                                claim.id,
                                "claim_count"
                              )}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              autoComplete="off"
                              name={`ClaimsCost${claim.id}`}
                              label="Claims Cost"
                              variant="outlined"
                              sx={{ width: "100%" }}
                              size="small"
                              value={claim.claim_amount}
                              onChange={handleChangeClaimHistoryItemText(
                                claim.id,
                                "claim_amount"
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={handleDeleteClaimsHistoryItem(claim.id)}
                              aria-label="delete claims history item"
                              color="inherit"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12}>
                  <ClaimsHistoryAddButton
                    onClick={onAddClaimsHistoryItem}
                    disabled={claimsHistory.length >= 3}
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>

          {/**
           * Optional Extras
           **/}
          <Grid item xs={12}>
            <Section subsection title="Optional Extras">
              <Grid container spacing={2}>
                {inputFields.optionalExtrasFields.map(
                  ({
                    id,
                    select,
                    input: { grid: inputGrid, ...restInput },
                  }) => (
                    <React.Fragment key={id}>
                      <Grid item {...select.grid}>
                        <FormControl sx={{ width: "100%" }}>
                          <FormHelperText>{select.label}</FormHelperText>
                          <Select
                            variant="outlined"
                            value={select.value}
                            onChange={select.handleChange as any}
                          >
                            {select.options.map(({ id, option }, count) => (
                              <MenuItem key={id} value={count}>
                                {select.optionDisplay
                                  ? select.optionDisplay(Number(option))
                                  : option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item {...inputGrid}>
                        <FormHelperText>&nbsp;</FormHelperText>
                        <ReadOnlyText
                          label={restInput.label}
                          value={
                            typeof restInput.value === "number"
                              ? decimalFormatter.format(restInput.value)
                              : "0.00"
                          }
                        />
                      </Grid>
                    </React.Fragment>
                  )
                )}
              </Grid>
            </Section>
          </Grid>

          {/**
           * Quote Calculations
           **/}
          <Grid item xs={12}>
            <Section subsection title="Quotation Totals">
              <CalculationsWrkCmp
                minPremium={minPremium}
                calculations={calculations}
                stampDuty={stampDuty}
              />
            </Section>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}

export default React.memo(WorkersCompensationForm, isPropsEqual);
